import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { newsArticles } from "@/mocks/news";

const SITE_URL = "https://beesfly.cn";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = newsArticles.find((item) => item.id === id);

  useSeo({
    title: article ? `${article.title} — 短剧爆料社` : "资讯未找到 — 短剧爆料社",
    description: article
      ? article.excerpt
      : "该资讯页面不存在，可返回爆料资讯频道浏览拍摄花絮与幕后制作故事。",
    keywords: article
      ? `${article.category},爆料短剧,拍摄花絮,幕后制作故事,${article.title}`
      : "爆料短剧,拍摄花絮",
    path: `/news/${id ?? ""}`,
    image: article?.cover,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.category,
            author: { "@type": "Organization", name: "短剧爆料社" },
            publisher: { "@type": "Organization", name: "短剧爆料社" },
            mainEntityOfPage: `${SITE_URL}/news/${article.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "爆料资讯", item: `${SITE_URL}/news` },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${SITE_URL}/news/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-content flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-file-unknow-line text-2xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这篇资讯不存在</h1>
          <Link
            to="/news"
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回爆料资讯
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const more = newsArticles.filter((item) => item.id !== article.id).slice(0, 3);

  return (
    <SiteLayout>
      <article className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "爆料资讯", to: "/news" },
            { label: article.category },
          ]}
        />

        <div className="mx-auto mt-6 max-w-3xl">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-accent-100 px-2.5 py-0.5 text-accent-900">
              {article.category}
            </span>
            <time>{article.date}</time>
            <span>·</span>
            <span>{article.author}</span>
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-5 border-l-2 border-primary-500 pl-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            {article.excerpt}
          </p>
        </div>

        <figure className="mx-auto mt-8 max-w-4xl">
          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} 幕后制作故事`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <figcaption className="mt-2.5 text-center text-xs text-foreground-500">
            文中配图用于内容讨论与信息整理
          </figcaption>
        </figure>

        <div className="mx-auto mt-10 max-w-3xl">
          {article.paragraphs.map((paragraph, index) => (
            <p
              key={index}
              className="mb-5 text-sm leading-loose text-foreground-700 md:text-base"
            >
              {paragraph}
            </p>
          ))}

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-200 pt-6">
            <span className="text-xs text-foreground-500">继续阅读</span>
            <Link
              to="/news"
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
            >
              更多{article.category}
            </Link>
            <Link
              to="/dramas"
              className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700 transition-colors hover:bg-primary-200"
            >
              去短剧库查演员表
            </Link>
          </div>
        </div>

        <section className="mx-auto mt-14 max-w-4xl border-t border-background-200 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
          <div className="mt-6 flex flex-col gap-4">
            {more.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 sm:flex-row sm:items-center"
              >
                <div className="h-24 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-20 sm:w-32">
                  <img
                    src={item.cover}
                    alt={`${item.title} 配图`}
                    title={item.title}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span>{item.category}</span>
                    <span>·</span>
                    <time>{item.date}</time>
                  </div>
                  <h3 className="mt-1.5 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                    {item.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </article>
    </SiteLayout>
  );
}